import { defineMessages, useIntl } from 'react-intl';
import { useMemo, useState } from 'react';

import sharedMessages from 'Components/utils/sharedMessages';
import EventHandler from 'Screens/Dashboard/Events/EventHandler/EventHandler';
import { ContextEvent, EventProvider, useEventContext } from 'Screens/Dashboard/Events/EventHandler/EntityEventContext';
import ConfirmationEditEventModal from 'Screens/Dashboard/Events/EditEventHandler/ConfirmationEditEventModal/ConfirmationEditEventModal';
import {
    useDeclineEventMutation,
    useDeleteEventMutation,
    useSelfAddToEventMutation,
    useUpdateEventMutation,
} from 'api/eventEndpoints';
import useGetCurrentEntityId from 'hooks/useGetCurrentEntityId';
import {
    RecurrenceType,
    UpdateEvent,
} from 'api/dtos/event';
import { combineDateAndTime } from 'utils/timeDateUtils';
import DeclineEventModal from './DeclineEventModal/DeclineEventModal';
import JoinEventModal from './JoinEventModal/JoinEventModal';
import DeleteEventModal from './DeleteEventModal/DeleteEventModal';
import useCurrentEntityUsesPoints from 'hooks/useCurrentEntityUsesPoints';
import { getEventOccurrenceDate } from 'utils/eventUtils';
import { normalizeHowLongForSubmit } from 'Screens/Dashboard/Events/EventHandler/inputs/EventRecurrence/recurrenceUtils';
import useGetUser from 'hooks/useGetUser';
import {
    getSelectedWeekdaysDateRange,
    sanitizeSelectedWeekdays,
} from './selectedWeekdaysUtils';

type Props = {
    event?: ContextEvent;
    onClose: () => void;
}

const EditEventHandlerContainer = ({
    event,
    onClose,
}: Props) => (
    <EventProvider initialState={event}>
        <EditEventHandler event={event} onClose={onClose} />
    </EventProvider>
);
const EditEventHandler = ({
    event,
    onClose,
}: Props) => {
    const intl = useIntl();
    const { values } = useEventContext();
    const currentEntityUsesPoints = useCurrentEntityUsesPoints();
    const user = useGetUser();

    const open = Boolean(event && true);
    const [updateEvent, { isLoading }] = useUpdateEventMutation();
    const currentEntityId = useGetCurrentEntityId();
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
    const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false);
    const [isJoinModalOpen, setIsJoinModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const selectedEntityId = values.entityId ?? currentEntityId;
    const selectedEntity = useMemo(
        () => user?.entities.find((entity) => entity.id === selectedEntityId),
        [selectedEntityId, user?.entities],
    );
    const usesPoints = selectedEntity?.usesPoints ?? currentEntityUsesPoints;

    const originalRecurrenceType = event ? event.recurrence.type : RecurrenceType.None;
    const isRecurring = originalRecurrenceType !== RecurrenceType.None;

    const [declineEvent, { isLoading: isDeclining }] = useDeclineEventMutation();
    const [selfAddToEvent, { isLoading: isJoining }] = useSelfAddToEventMutation();
    const [deleteEvent, { isLoading: isDeleting }] = useDeleteEventMutation();

    const onCloseRequest = () => {
        onClose();
    };

    const handleSubmitRequest = async () => {
        if (!isRecurring) {
            await handleSubmit(UpdateEvent.Single);
            return;
        }
        setIsConfirmationModalOpen(true);
    };

    const handleSubmit = async (mode: UpdateEvent, selectedWeekdays: number[] = []) => {
        if (!currentEntityId || !event?.id) return;

        const isSelectedWeekdaysMode = mode === UpdateEvent.SelectedWeekdays;
        const occurrenceDate = getEventOccurrenceDate(event);
        const editedDateFrom = combineDateAndTime(values.dateFrom, values.timeFrom);
        const dateToValue = values.isMultiDay ? values.dateTo : values.dateFrom;
        const editedDateTo = combineDateAndTime(dateToValue, values.timeTo);
        const selectedWeekdaysDateRange = getSelectedWeekdaysDateRange(
            occurrenceDate,
            values.dateFrom,
            values.dateTo,
            values.timeFrom,
            values.timeTo,
            values.isMultiDay,
        );
        const dateFrom = isSelectedWeekdaysMode ? selectedWeekdaysDateRange.dateFrom : editedDateFrom;
        const dateTo = isSelectedWeekdaysMode ? selectedWeekdaysDateRange.dateTo : editedDateTo;
        const eventKind = values.kind ?? (values.isGlobal ? 'Global' : 'Local');
        const isGlobalLocalContext = eventKind === 'Global' && Boolean(values.id) && !values.isGlobalSource;
        const recurrence = isSelectedWeekdaysMode ? event.recurrence : values.recurrence;
        const howLong = normalizeHowLongForSubmit(
            recurrence,
            isSelectedWeekdaysMode ? event.howLong : values.howLong,
        );
        const sanitizedWeekdays = isSelectedWeekdaysMode
            ? sanitizeSelectedWeekdays(selectedWeekdays, event.recurrence)
            : [];

        if (isSelectedWeekdaysMode && sanitizedWeekdays.length === 0) return;

        try {
            await updateEvent({
                entityId: currentEntityId,
                data: {
                    id: event.id,
                    title: values.title,
                    description: values.description,
                    eventPlace: values.eventPlace,
                    dateFrom: dateFrom ? dateFrom.format() : values.dateFrom,
                    dateTo: dateTo ? dateTo.format() : values.dateTo,
                    originalDateFrom: occurrenceDate,
                    ...(isSelectedWeekdaysMode ? {
                        originalOccurrenceDate: occurrenceDate,
                        selectedWeekdays: sanitizedWeekdays,
                    } : {}),
                    allDay: values.allDay,
                    visibleForGuests: values.visibleForGuests,
                    email: '',
                    pointsPerEvent: usesPoints ? values.pointsPerEvent : 0,
                    optionalPointsPerEvent: usesPoints ? values.optionalPointsPerEvent : null,
                    absencePenaltyPoints: usesPoints ? values.absencePenaltyPoints ?? null : null,
                    qrConfirmationWindowMinutes: usesPoints ? values.qrConfirmationWindowMinutes : 60,
                    attendeeReminderMinutesBefore: values.attendeeReminderMinutesBefore ?? null,
                    recurrence,
                    ...(howLong ? { howLong } : {}),
                    categoryIds: values.categoryIds,
                    attendees: eventKind === 'Global' && !isGlobalLocalContext ? [] : values.attendees,
                    kind: eventKind,
                    scope: eventKind === 'Global' ? values.scope ?? 'GlobalAllChildren' : 'LocalOnly',
                    targetEntityIds: eventKind === 'Global' && values.scope === 'GlobalSelectedChildren'
                        ? values.targetEntityIds ?? []
                        : [],
                    color: values.color ?? '#3174AD',
                    priests: values.priests ?? [],
                    priestIds: values.priestIds ?? [],
                },
                mode,
            }).unwrap();
            setIsConfirmationModalOpen(false);
            onClose();
        } catch (e) {
            console.log(e);
        }
    };

    const handleDeclineConfirm = async (reason: string, mode?: UpdateEvent) => {
        if (!currentEntityId || !values.id) return;
        await declineEvent({
            entityId: currentEntityId,
            eventId: values.id,
            declineReason: reason,
            mode,
            occurrenceDate: getEventOccurrenceDate(values),
        });
        setIsDeclineModalOpen(false);
        onClose();
    };

    const handleJoinConfirm = async (mode?: UpdateEvent) => {
        if (!currentEntityId || !values.id) return;
        await selfAddToEvent({
            entityId: currentEntityId,
            eventId: values.id,
            mode,
            occurrenceDate: getEventOccurrenceDate(values),
        });
        setIsJoinModalOpen(false);
        onClose();
    };

    const handleDeleteConfirm = async (mode: UpdateEvent) => {
        if (!currentEntityId || !values.id) return;
        await deleteEvent({
            entityId: currentEntityId,
            id: values.id,
            mode,
            occurrenceDate: getEventOccurrenceDate(values),
        });
        setIsDeleteModalOpen(false);
        onClose();
    };

    const isAnySecondaryModalOpen = isConfirmationModalOpen || isDeclineModalOpen || isJoinModalOpen || isDeleteModalOpen;

    return (
        <>
            <EventHandler
                open={open && !isAnySecondaryModalOpen}
                isLoading={isLoading}
                modalHeading={intl.formatMessage(messages.editEvent)}
                primaryButtonText={intl.formatMessage(sharedMessages.edit)}
                handleSubmit={handleSubmitRequest}
                onDeleteRequest={() => { setIsDeleteModalOpen(true); }}
                onCloseRequest={onCloseRequest}
            />
            <ConfirmationEditEventModal
                event={event}
                values={values}
                isOpen={isConfirmationModalOpen}
                onClose={() => { setIsConfirmationModalOpen(false); }}
                onFinalSubmit={handleSubmit}
            />
            <DeclineEventModal
                isOpen={isDeclineModalOpen}
                onClose={() => { setIsDeclineModalOpen(false); }}
                onConfirm={handleDeclineConfirm}
                isRecurring={isRecurring}
                isLoading={isDeclining}
            />
            <JoinEventModal
                isOpen={isJoinModalOpen}
                onClose={() => { setIsJoinModalOpen(false); }}
                onConfirm={handleJoinConfirm}
                isRecurring={isRecurring}
                isLoading={isJoining}
            />
            <DeleteEventModal
                isOpen={isDeleteModalOpen}
                onClose={() => { setIsDeleteModalOpen(false); }}
                onConfirm={handleDeleteConfirm}
                isRecurring={isRecurring}
                isLoading={isDeleting}
            />
        </>
    );
};

const messages = defineMessages({
    editEvent: {
        id: 'src.Screens.Dashboard.Events.EventHandler.EventHandler.EditEventHandler.EditEventHandler.editEvent',
        defaultMessage: 'Edytuj wydarzenie',
    },
});

export default EditEventHandlerContainer;
